import { useState, useEffect, useRef, useCallback } from "react";
import { Check, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../ui/button";

import AudioMicButton from "./AudioMicButton";
import { AudioProcessingModal } from "./AudioProcessingModal";
import { createAudioRecorder, AudioRecorder } from "@/lib/audioRecorder";

type AudioInputTrackProps = {
  onRecordingComplete?: (audioBlob: Blob) => Promise<void>;
  onError?: (error: unknown) => void;
};

export function AudioInputTrack({
  onRecordingComplete,
  onError,
}: AudioInputTrackProps) {
  const navigate = useNavigate();
  const MAX_RECORDING_SECONDS = 10;
  const STOP_DELAY_SECONDS = 0.5; // Delay before actually stopping recording

  // State
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isProcessingAudio, setIsProcessingAudio] = useState(false);

  // Audio recorder ref
  const audioRecorderRef = useRef<AudioRecorder | null>(null);

  const processAudio = useCallback(
    async (audioBlob: Blob) => {
      try {
        // isProcessingAudio is already set to true by onProcessingStart
        await onRecordingComplete?.(audioBlob);
      } catch (error) {
        console.error("Audio processing error:", error);
        onError?.(error);
      } finally {
        setIsProcessingAudio(false);
      }
    },
    [onRecordingComplete, onError]
  );

  // Initialize audio recorder
  useEffect(() => {
    audioRecorderRef.current = createAudioRecorder({
      maxRecordingTime: MAX_RECORDING_SECONDS,
      stopDelaySeconds: STOP_DELAY_SECONDS,
      onRecordingStart: () => {
        console.log("Recording started");
        setIsRecording(true);
        setRecordingTime(0);
      },
      onProcessingStart: () => {
        console.log("Processing started - showing modal immediately");
        setIsRecording(false);
        setIsProcessingAudio(true);
      },
      onRecordingStop: (audioBlob: Blob) => {
        console.log("Recording stopped, processing audio...");
        processAudio(audioBlob);
      },
      onRecordingCancel: () => {
        console.log("Recording cancelled");
        setIsRecording(false);
        setRecordingTime(0);
      },
      onTimeUpdate: (time: number) => {
        setRecordingTime(time);
      },
      onAutoStop: () => {
        console.log(
          `Auto-stopping recording after ${MAX_RECORDING_SECONDS} seconds`
        );
      },
      onError: (error: unknown) => {
        console.error("Audio recording error:", error);
        setIsRecording(false);
        setIsProcessingAudio(false);
        onError?.(error);
      },
    });

    // Cleanup on unmount
    return () => {
      audioRecorderRef.current?.destroy();
    };
  }, [processAudio, onError]);

  const toggleRecording = (shouldProcess: boolean = true) => {
    if (!audioRecorderRef.current) return;

    if (isRecording) {
      if (shouldProcess) {
        audioRecorderRef.current.stopRecording();
      } else {
        audioRecorderRef.current.cancelRecording();
      }
    } else {
      audioRecorderRef.current.startRecording();
    }
  };

  return (
    <>
      <div className="w-full px-6 mb-6">
        <div className="px-5 pt-7 pb-4 min-h-[250px]">
          <div
            className="flex flex-col items-center"
            onClick={() => toggleRecording(true)}
            style={{ cursor: "pointer" }}
          >
            <AudioMicButton isRecording={isRecording} />

            <h3 className="mb-1">
              {isRecording ? (
                <span className="text-xl font-normal text-tx-default">
                  Recording...{" "}
                  {Math.floor(MAX_RECORDING_SECONDS - recordingTime)}s left
                </span>
              ) : isProcessingAudio ? (
                <span className="text-xl font-normal text-tx-dark">
                  Processing...
                </span>
              ) : (
                <span className="text-4xl font-black text-tx-dark">
                  Tap to Track
                </span>
              )}
            </h3>
          </div>

          {isRecording && (
            <div className="flex justify-center items-center my-2 gap-3 px-4">
              <Button
                variant="outline"
                className="px-5"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleRecording(false);
                }}
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button
                variant="primary"
                className="px-5"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleRecording(true);
                }}
              >
                <Check className="h-4 w-4 mr-2" />
                Finish
              </Button>
            </div>
          )}

          {!isRecording && !isProcessingAudio && (
            <p className="text-center text-sm font-medium text-tx-default/80">
              Speak your set. We'll log it for you.
            </p>
          )}
        </div>

        <div className="w-full h-[50px]">
          {!isRecording && !isProcessingAudio && (
            <div className="flex justify-center w-full">
              <Button
                onClick={() => navigate("/track/manual")}
                variant="outline"
                size="sm"
                className="font-normal items-center"
              >
                <svg
                  className="mr-2 w-5 h-5"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9.99995 14H14M6 10H6.05M10 10H10.05M14 10H14.05M18 10H18.05M18 14H18.05M6 14H6.05M13 5H8.4C6.15979 5 5.03968 5 4.18404 5.43597C3.43139 5.81947 2.81947 6.43139 2.43597 7.18404C2 8.03968 2 9.15979 2 11.4V12.6C2 14.8402 2 15.9603 2.43597 16.816C2.81947 17.5686 3.43139 18.1805 4.18404 18.564C5.03968 19 6.15979 19 8.4 19H15.6C17.8402 19 18.9603 19 19.816 18.564C20.5686 18.1805 21.1805 17.5686 21.564 16.816C22 15.9603 22 14.8402 22 12.6V9.5C22 9.03566 22 8.80349 21.9807 8.60793C21.7937 6.70882 20.2912 5.20631 18.3921 5.01926C18.1965 5 17.9643 5 17.5 5V5H17M6.25 10C6.25 10.1381 6.13807 10.25 6 10.25C5.86193 10.25 5.75 10.1381 5.75 10C5.75 9.86193 5.86193 9.75 6 9.75C6.13807 9.75 6.25 9.86193 6.25 10ZM10.25 10C10.25 10.1381 10.1381 10.25 10 10.25C9.86193 10.25 9.75 10.1381 9.75 10C9.75 9.86193 9.86193 9.75 10 9.75C10.1381 9.75 10.25 9.86193 10.25 10ZM14.25 10C14.25 10.1381 14.1381 10.25 14 10.25C13.8619 10.25 13.75 10.1381 13.75 10C13.75 9.86193 13.8619 9.75 14 9.75C14.1381 9.75 14.25 9.86193 14.25 10ZM18.25 10C18.25 10.1381 18.1381 10.25 18 10.25C17.8619 10.25 17.75 10.1381 17.75 10C17.75 9.86193 17.8619 9.75 18 9.75C18.1381 9.75 18.25 9.86193 18.25 10ZM18.25 14C18.25 14.1381 18.1381 14.25 18 14.25C17.8619 14.25 17.75 14.1381 17.75 14C17.75 13.8619 17.8619 13.75 18 13.75C18.1381 13.75 18.25 13.8619 18.25 14ZM6.25 14C6.25 14.1381 6.13807 14.25 6 14.25C5.86193 14.25 5.75 14.1381 5.75 14C5.75 13.8619 5.86193 13.75 6 13.75C6.13807 13.75 6.25 13.8619 6.25 14Z"
                    stroke="#2F384C"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                or, enter manually
              </Button>
            </div>
          )}
        </div>
      </div>

      {isProcessingAudio && <AudioProcessingModal />}
    </>
  );
}
